/**
 * The name to save a download under, from its Content-Disposition.
 *
 * `filename*` (RFC 6266/5987) first: it is the one that carries å, ä and ö, and the one every
 * browser prefers. Then `filename`, which Spring 6.1 RFC 2047 encodes when given a charset -
 * `=?UTF-8?Q?diremo-bi-2026-10-29.xlsx?=` - and which was once saved under that very name.
 * Without either, `file` with an extension from the content type, so that the saved file still
 * opens in the program it is for.
 */
export function downloadFileName(contentDisposition: string | null, contentType?: string): string {
  const header = contentDisposition ?? '';

  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[2].trim().replace(/^"|"$/g, ''));
    } catch {
      // A malformed escape: fall through to the plain name.
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
  if (plain) {
    const name = (plain[2] ?? plain[1]).trim();
    if (name) return decodeEncodedWord(name);
  }

  return `file${EXTENSIONS[(contentType ?? '').split(';')[0].trim()] ?? ''}`;
}

const EXTENSIONS: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'application/pdf': '.pdf',
  'text/csv': '.csv',
};

/** `=?UTF-8?Q?a=C3=A5?=` or `=?UTF-8?B?...?=` (RFC 2047) to text; anything else unchanged. */
function decodeEncodedWord(value: string): string {
  const word = /^=\?([^?]+)\?([QqBb])\?(.*)\?=$/.exec(value);
  if (!word) return value;
  const [, charset, encoding, text] = word;
  try {
    const bytes =
      encoding.toUpperCase() === 'B'
        ? Uint8Array.from(atob(text), (c) => c.charCodeAt(0))
        : Uint8Array.from(
            text
              .replace(/_/g, ' ')
              .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))),
            (c) => c.charCodeAt(0)
          );
    // Bytes to text without TextDecoder, which not every runtime the app is tested in has: UTF-8
    // through percent-escapes, anything else - in practice ISO-8859-1 - a byte per character.
    return /^utf-?8$/i.test(charset)
      ? decodeURIComponent(Array.from(bytes, (b) => `%${b.toString(16).padStart(2, '0')}`).join(''))
      : String.fromCharCode(...Array.from(bytes));
  } catch {
    return value;
  }
}
